import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useDataCache, useCache } from '../contexts/DataCacheContext';
import { getAthletePrograms, setActiveProgramForAthlete, deleteProgram, unassignAthleteProgram, duplicateProgram, updateProgram } from '../lib/database';
import { useScrollLock } from '../hooks/useScrollLock';
import { useSwipeBack } from '../hooks/useSwipeBack';
import { useSheetDrag } from '../hooks/useSheetDrag';
import SkeletonLoader from './SkeletonLoader';
import BottomNav from './BottomNav';
import './AthletePrograms.css';

function AthletePrograms() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const toast = useToast();
  const { invalidate: invalidateCache } = useCache();
  // La dashboard tiene in cache (TTL 60s) scheda attiva e prossimo allenamento:
  // ogni cambiamento fatto qui deve buttarla, o tornando indietro si vede lo
  // stato precedente finché il TTL non scade.
  const invalidateDashboard = () => invalidateCache(`athlete-dashboard-${profile?.id}`);
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  const [menuProgram, setMenuProgram] = useState(null);
  const [showRenameSheet, setShowRenameSheet] = useState(false);
  const [renamingProgram, setRenamingProgram] = useState(null);
  const [confirmDeleteProgram, setConfirmDeleteProgram] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const { handleTouchStart, handleTouchEnd } = useSwipeBack(() => navigate('/athlete'));

  const fetchPrograms = useCallback(
    () => getAthletePrograms(profile?.id),
    [profile?.id]
  );
  const { data: programs, loading, refresh: loadPrograms } = useDataCache(
    profile?.id ? `athlete-programs-${profile.id}` : null,
    fetchPrograms,
    120000
  );

  const handleViewProgram = (programId, creatorType, isActive) => {
    // Allow editing of own programs OR active programs (even from coach)
    if (creatorType === 'athlete' || isActive) {
      navigate(`/athlete/programs/${programId}/edit`);
    } else {
      navigate(`/athlete/programs/${programId}/view`);
    }
  };

  const handleSetActive = async (athleteProgramId) => {
    try {
      await setActiveProgramForAthlete(profile.id, athleteProgramId);
      invalidateDashboard();
      await loadPrograms();
      setMenuProgram(null);
      toast.success('Programma attivato');
    } catch (error) {
      console.error('Error setting active program:', error);
      toast.error('Errore nell\'attivazione del programma');
    }
  };

  const handleDeleteProgram = async () => {
    if (!confirmDeleteProgram || !confirmDeleteProgram.program) return;
    setActionLoading(true);
    try {
      await deleteProgram(confirmDeleteProgram.program.id);
      invalidateDashboard();
      setConfirmDeleteProgram(null);
      await loadPrograms();
      toast.success('Scheda eliminata');
    } catch (error) {
      console.error('Error deleting program:', error);
      toast.error('Errore nell\'eliminazione della scheda');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeactivateProgram = async () => {
    try {
      await unassignAthleteProgram(profile.id);
      invalidateDashboard();
      setConfirmDeactivate(false);
      await loadPrograms();
      toast.success('Scheda disattivata');
    } catch (error) {
      console.error('Error deactivating program:', error);
      toast.error('Errore nella disattivazione della scheda');
    }
  };

  const handleStartProgram = () => {
    navigate('/athlete/program');
  };

  const handleDuplicateProgram = async (ap) => {
    setActionLoading(true);
    try {
      await duplicateProgram(ap.program.id, profile.id, 'athlete');
      invalidateDashboard();
      await loadPrograms();
      setMenuProgram(null);
      toast.success('Scheda duplicata');
    } catch (error) {
      console.error('Error duplicating program:', error);
      toast.error('Errore nella duplicazione della scheda');
    } finally{
      setActionLoading(false);
    }
  };

  const handleRenameProgram = async (programId, newName) => {
    setActionLoading(true);
    try {
      await updateProgram(programId, { name: newName });
      invalidateDashboard();
      await loadPrograms();
      setShowRenameSheet(false);
      setRenamingProgram(null);
      toast.success('Scheda rinominata');
    } catch (error) {
      console.error('Error renaming program:', error);
      toast.error('Errore nella rinomina della scheda');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="sp-container page-with-bottom-nav">
        <header className="sp-header">
          <span className="sp-header-title">Le tue Schede</span>
        </header>
        <div style={{ padding: '20px' }}>
          <SkeletonLoader variant="list" count={3} />
        </div>
        <BottomNav />
      </div>
    );
  }

  const programsList = programs || [];
  const activeProgram = programsList.find(p => p.is_active);
  const coachPrograms = programsList.filter(p => p.program?.creator_type === 'coach' && !p.is_active);
  const selfPrograms = programsList.filter(p => p.program?.creator_type === 'athlete' && !p.is_active);

  return (
    <div
      className="sp-container page-with-bottom-nav"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Sticky Header */}
      <header className="sp-header">
        <button className="sp-header-back" onClick={() => navigate('/athlete')} aria-label="Indietro">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <span className="sp-header-title">Le tue Schede</span>
      </header>

      <main className="sp-main">
        {/* Active Program Card */}
        {activeProgram && (
          <div className="sp-active-card">
            <div className="sp-active-top">
              <span className="sp-active-badge">ATTIVA</span>
              <span className="sp-active-name">{activeProgram.program.name}</span>
            </div>
            {activeProgram.program.description && (
              <p className="sp-active-desc">{activeProgram.program.description}</p>
            )}
            <div className="sp-active-meta">
              {activeProgram.program.creator?.name && activeProgram.program.creator_type === 'coach' && (
                <span className="sp-meta-chip">Coach {activeProgram.program.creator.name}</span>
              )}
              {activeProgram.program.creator_type === 'athlete' && (
                <span className="sp-meta-chip">Creata da te</span>
              )}
            </div>
            <div className="sp-active-actions">
              <button className="sp-btn-primary" onClick={handleStartProgram}>
                Inizia
              </button>
              <button
                className="sp-btn-secondary"
                onClick={() => handleViewProgram(activeProgram.program.id, activeProgram.program.creator_type, true)}
              >
                {activeProgram.program.creator_type === 'athlete' ? 'Modifica' : 'Modifica Scheda Attiva'}
              </button>
              {confirmDeactivate ? (
                <div className="sp-deactivate-confirm">
                  <span className="sp-deactivate-text">Disattivare?</span>
                  <button className="sp-btn-deactivate-yes" onClick={handleDeactivateProgram}>Sì</button>
                  <button className="sp-btn-deactivate-no" onClick={() => setConfirmDeactivate(false)}>No</button>
                </div>
              ) : (
                <button
                  className="sp-btn-deactivate"
                  onClick={() => setConfirmDeactivate(true)}
                >
                  Disattiva
                </button>
              )}
            </div>
          </div>
        )}

        {/* Coach Programs */}
        {coachPrograms.length > 0 && (
          <section className="sp-section">
            <h2 className="sp-section-title">Schede del Coach</h2>
            <div className="sp-list">
              {coachPrograms.map(ap => (
                <div key={ap.id} className="sp-row">
                  <div className="sp-row-info" onClick={() => handleViewProgram(ap.program.id, ap.program.creator_type, false)}>
                    <span className="sp-row-name">{ap.program.name}</span>
                    {ap.program.creator?.name && (
                      <span className="sp-row-sub">Coach {ap.program.creator.name}</span>
                    )}
                  </div>
                  <div className="sp-row-actions">
                    <button
                      className="sp-btn-row-primary"
                      onClick={() => handleSetActive(ap.id)}
                    >
                      Imposta
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Self Programs */}
        {selfPrograms.length > 0 && (
          <section className="sp-section">
            <h2 className="sp-section-title">Create da te</h2>
            <div className="sp-list">
              {selfPrograms.map(ap => (
                <div key={ap.id} className="sp-row">
                  <div className="sp-row-info" onClick={() => handleViewProgram(ap.program.id, 'athlete', false)}>
                    <span className="sp-row-name">{ap.program.name}</span>
                  </div>
                  <button
                    className="sp-btn-row-menu"
                    onClick={() => setMenuProgram(ap)}
                    aria-label="Azioni"
                  >
                    &#x22EF;
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Empty state */}
        {programsList.length === 0 && (
          <div className="sp-empty">
            <p className="sp-empty-title">Nessuna scheda</p>
            <p className="sp-empty-sub">Crea una scheda o chiedi al tuo coach</p>
          </div>
        )}

        {/* Create button */}
        <button className="sp-btn-create" onClick={() => navigate('/athlete/programs/new')}>
          + Crea Nuova Scheda
        </button>
      </main>

      <BottomNav />

      {/* Bottom Sheet: Program Actions */}
      <SelfProgramActionsSheet
        isOpen={!!menuProgram}
        program={menuProgram}
        loading={actionLoading}
        onClose={() => setMenuProgram(null)}
        onEdit={() => { handleViewProgram(menuProgram?.program.id, 'athlete', false); setMenuProgram(null); }}
        onDuplicate={() => menuProgram && handleDuplicateProgram(menuProgram)}
        onRename={() => {
          setRenamingProgram(menuProgram);
          setMenuProgram(null);
          setShowRenameSheet(true);
        }}
        onSetActive={() => menuProgram && handleSetActive(menuProgram.id)}
        onDelete={() => {
          setConfirmDeleteProgram(menuProgram);
          setMenuProgram(null);
        }}
      />

      {/* Bottom Sheet: Rename */}
      <RenameProgramSheet
        isOpen={showRenameSheet}
        program={renamingProgram}
        loading={actionLoading}
        onClose={() => { setShowRenameSheet(false); setRenamingProgram(null); }}
        onRename={handleRenameProgram}
      />

      {/* Delete Confirmation */}
      {confirmDeleteProgram && (
        <DeleteConfirmSheet
          program={confirmDeleteProgram}
          loading={actionLoading}
          onClose={() => setConfirmDeleteProgram(null)}
          onConfirm={handleDeleteProgram}
        />
      )}
    </div>
  );
}

/* ===== Bottom Sheet: Self Program Actions ===== */
function SelfProgramActionsSheet({ isOpen, program, loading, onClose, onEdit, onDuplicate, onRename, onSetActive, onDelete }) {
  useScrollLock(isOpen);
  const { contentRef, sheetStyle, overlayOpacity, handleTouchStart, handleTouchMove, handleTouchEnd, handleClose } = useSheetDrag(onClose);

  if (!isOpen || !program || !program.program) return null;

  return (
    <div className="sp-sheet-overlay" style={{ backgroundColor: `rgba(0,0,0,${overlayOpacity})` }} onClick={handleClose}>
      <div ref={contentRef} className="sp-sheet sp-sheet--compact" style={sheetStyle}
           onClick={(e) => e.stopPropagation()}
           onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
        <div className="sp-sheet-handle" />
        <div className="sp-sheet-header">
          <h2 className="sp-sheet-title">{program.program.name}</h2>
          <button className="sp-sheet-close" onClick={handleClose}>&#x2715;</button>
        </div>
        <div className="sp-sheet-body sp-action-list">
          <button className="sp-action-item" onClick={onEdit} disabled={loading}>
            <span className="sp-action-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </span>
            <span>Modifica</span>
          </button>
          <button className="sp-action-item" onClick={onDuplicate} disabled={loading}>
            <span className="sp-action-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
            </span>
            <span>{loading ? 'Duplicazione...' : 'Duplica'}</span>
          </button>
          <button className="sp-action-item" onClick={onRename} disabled={loading}>
            <span className="sp-action-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
              </svg>
            </span>
            <span>Rinomina</span>
          </button>
          <button className="sp-action-item" onClick={onSetActive} disabled={loading}>
            <span className="sp-action-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </span>
            <span>Imposta come attiva</span>
          </button>
          <button className="sp-action-item sp-action-item--danger" onClick={onDelete} disabled={loading}>
            <span className="sp-action-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
            </span>
            <span>Elimina</span>
          </button>
        </div>
      </div>
    </div>
  );
}

/* ===== Bottom Sheet: Rename Program ===== */
function RenameProgramSheet({ isOpen, program, loading, onClose, onRename }) {
  useScrollLock(isOpen);
  const { contentRef, sheetStyle, overlayOpacity, handleTouchStart, handleTouchMove, handleTouchEnd, handleClose } = useSheetDrag(onClose);
  const [name, setName] = useState('');

  useEffect(() => {
    if (program?.program) setName(program.program.name);
  }, [program]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (name.trim() && program?.program) {
      onRename(program.program.id, name.trim());
    }
  };

  if (!isOpen || !program || !program.program) return null;

  return (
    <div className="sp-sheet-overlay" style={{ backgroundColor: `rgba(0,0,0,${overlayOpacity})` }} onClick={handleClose}>
      <div ref={contentRef} className="sp-sheet sp-sheet--compact" style={sheetStyle}
           onClick={(e) => e.stopPropagation()}
           onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
        <div className="sp-sheet-handle" />
        <div className="sp-sheet-header">
          <h2 className="sp-sheet-title">Rinomina Scheda</h2>
          <button className="sp-sheet-close" onClick={handleClose}>&#x2715;</button>
        </div>
        <form onSubmit={handleSubmit} className="sp-sheet-body">
          <div className="sp-field">
            <label>Nome</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nome scheda"
              required
              autoFocus
              disabled={loading}
            />
          </div>
          <div className="sp-sheet-actions">
            <button type="button" className="sp-sheet-btn-cancel" onClick={handleClose} disabled={loading}>
              Annulla
            </button>
            <button
              type="submit"
              className="sp-sheet-btn-submit"
              disabled={loading || !name.trim() || name.trim() === program.program.name}
            >
              {loading ? 'Salvataggio...' : 'Salva'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ===== Bottom Sheet: Delete Confirmation ===== */
function DeleteConfirmSheet({ program, loading, onClose, onConfirm }) {
  useScrollLock(true);
  const { contentRef, sheetStyle, overlayOpacity, handleTouchStart, handleTouchMove, handleTouchEnd, handleClose } = useSheetDrag(onClose);

  if (!program || !program.program) return null;

  return (
    <div className="sp-sheet-overlay" style={{ backgroundColor: `rgba(0,0,0,${overlayOpacity})` }} onClick={handleClose}>
      <div ref={contentRef} className="sp-sheet sp-sheet--compact" style={sheetStyle}
           onClick={(e) => e.stopPropagation()}
           onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
        <div className="sp-sheet-handle" />
        <div className="sp-sheet-header">
          <h2 className="sp-sheet-title">Elimina Scheda</h2>
          <button className="sp-sheet-close" onClick={handleClose}>&#x2715;</button>
        </div>
        <div className="sp-sheet-body">
          <p className="sp-delete-msg">
            Vuoi eliminare <strong>{program.program.name}</strong>?
          </p>
          <div className="sp-sheet-actions">
            <button className="sp-sheet-btn-cancel" onClick={handleClose} disabled={loading}>
              Annulla
            </button>
            <button className="sp-sheet-btn-danger" onClick={onConfirm} disabled={loading}>
              {loading ? 'Eliminazione...' : 'Elimina'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AthletePrograms;
